import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Toast from '../Toast';
import { API_ERROR_EVENT, ApiErrorEventDetail } from 'core/utils/queryErrorHandling';

export type ToastType = 'success' | 'error';

export interface ToastItem {
	id: string;
	type: ToastType;
	message: string;
	duration: number;
}

interface IToastProvider {
	toasts: ToastItem[];
	showToast: (type: ToastType, text?: string, timeout?: number) => void;
	closeToast: (id: string) => void;
}

const ToastContext = React.createContext<IToastProvider>(undefined!);

const DEFAULT_MESSAGES: Record<ToastType, string> = {
	success: 'Success!',
	error: 'Something went wrong!',
};

const MAX_VISIBLE_TOASTS = 5;

export function ToastContextProvider({ children }: { children: React.ReactNode }): JSX.Element {
	const [toasts, setToasts] = useState<ToastItem[]>([]);
	const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

	const clearToastTimeout = useCallback((id: string) => {
		const timeout = timeoutsRef.current.get(id);
		if (timeout) {
			clearTimeout(timeout);
			timeoutsRef.current.delete(id);
		}
	}, []);

	const closeToast = useCallback(
		(id: string) => {
			clearToastTimeout(id);
			setToasts((prev) => prev.filter((toast) => toast.id !== id));
		},
		[clearToastTimeout]
	);

	const showToast = useCallback(
		(type: ToastType, text?: string, timeout = 3000) => {
			const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
			const message = text?.trim() || DEFAULT_MESSAGES[type];
			const duration = timeout > 0 ? timeout : 3000;
			const newToast: ToastItem = { id, type, message, duration };

			setToasts((prev) => {
				const next = [...prev, newToast];
				if (next.length <= MAX_VISIBLE_TOASTS) return next;
				next.slice(0, next.length - MAX_VISIBLE_TOASTS).forEach((toast) => clearToastTimeout(toast.id));
				return next.slice(-MAX_VISIBLE_TOASTS);
			});

			const timer = setTimeout(() => closeToast(id), duration);
			timeoutsRef.current.set(id, timer);
		},
		[clearToastTimeout, closeToast]
	);

	// REL-002: surface react-query / API failures globally (debounced in notifyApiError)
	useEffect(() => {
		const onApiError = (event: Event) => {
			const detail = (event as CustomEvent<ApiErrorEventDetail>).detail;
			if (!detail?.message) return;
			showToast('error', detail.message, 5000);
		};
		window.addEventListener(API_ERROR_EVENT, onApiError);
		return () => window.removeEventListener(API_ERROR_EVENT, onApiError);
	}, [showToast]);

	const value = useMemo(() => ({ toasts, showToast, closeToast }), [toasts, showToast, closeToast]);

	return (
		<ToastContext.Provider value={value}>
			{children}
			<Toast />
		</ToastContext.Provider>
	);
}

export const useToastContext = (): IToastProvider => React.useContext(ToastContext);
export const useProviderFunctions = useToastContext;
